import PropTypes from 'prop-types';
import classNames from 'classnames';

import Image from '../Image/Image';
import MaterialIcon from '../MaterialIcon/MaterialIcon';
import SkeletonLoader from '../SkeletonLoader/SkeletonLoader';
import DataErrorItem from '../DataErrorItem/DataErrorItem';
import { t } from '../../i18n';
import { formatValueWithScale } from '../../utils/helpers';

import s from './SingleDataValueItem.module.scss';

const SingleDataValueItem = ({
    hasEditorialImage,
    image,
    icon = null,
    isLoading = false,
    errorFetchingData = false,
    title = '',
    description = '',
    value = '',
    unit = '',
    change = null,
    valueFormat = 'none',
    decimalPrecision = 2,
    isUnitPercentage = false,
    isUnitLongText = false,
    isEmpty = false,
}) => {
    const formattedValue = formatValueWithScale(
        value,
        valueFormat,
        decimalPrecision
    );
    const displayedUnit = formattedValue.unit || unit;
    const classes = classNames(
        s.Root,
        { [s['Root--Percentage']]: isUnitPercentage },
        { [s['Root--LongUnitText']]: isUnitLongText },
        { [s['Root--Empty']]: isEmpty }
    );

    return (
        <div className={classes}>
            <div className={s.Container}>
                {hasEditorialImage && (
                    <div className={s.Image}>
                        <Image {...image} />
                    </div>
                )}
                {icon && !hasEditorialImage && (
                    <div className={s.Image}>
                        <MaterialIcon name={icon} />
                    </div>
                )}
                {isLoading && (
                    <div className={s.LoaderWrapper}>
                        <SkeletonLoader />
                    </div>
                )}
                {errorFetchingData && (
                    <DataErrorItem
                        title={title}
                        description={t('general.errorFetchingData')}
                    />
                )}
                {!isLoading && !errorFetchingData && (
                    <div className={s.Text}>
                        <div className={s.TitleWrapper}>
                            <h3 className={s.Title}>
                                {title}
                                {description && (
                                    <span className={s.Description}>
                                        {description}
                                    </span>
                                )}
                            </h3>
                        </div>
                        <div className={s.DataWrapper}>
                            <h4 className={s.Value}>{formattedValue.value}</h4>
                            {isUnitPercentage && (
                                <span className={s.Percentage}>%</span>
                            )}
                            <span className={s.Unit}>{displayedUnit}</span>
                            {change && (
                                <span
                                    className={classNames(s.Change, {
                                        [s['Change--Negative']]:
                                            String(change).startsWith('-'),
                                    })}>
                                    ({change})
                                </span>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

SingleDataValueItem.propTypes = {
    hasEditorialImage: PropTypes.bool,
    image: PropTypes.object,
    icon: PropTypes.string,
    isLoading: PropTypes.bool,
    errorFetchingData: PropTypes.bool,
    title: PropTypes.string,
    description: PropTypes.string,
    value: PropTypes.string,
    unit: PropTypes.string,
    change: PropTypes.string,
    valueFormat: PropTypes.string,
    decimalPrecision: PropTypes.number,
    isUnitPercentage: PropTypes.bool,
    isUnitLongText: PropTypes.bool,
    isEmpty: PropTypes.bool,
};

export default SingleDataValueItem;
